import React, { useState } from "react";
import styled from "@emotion/styled";
import { useTranslation } from "react-i18next";
import { useApiRequest } from "@utils/ApiRequest";
import { useQuery } from "@tanstack/react-query";

const CardContainer = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: "1rem",
  padding: "2.0rem",
});

const NameWrapper = styled.div(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "0.4rem",

  ".time": {
    color: theme.color.light_gray,
    fontSize: "1.4rem",
    lineHeight: "1.6rem",
    fontWeight: theme.fontWeight.medium,
  },
}));

const NameBox = styled.div(({ theme }) => ({
  display: "flex",
  alignItems: "flex-start",
  gap: "2rem",
  color: theme.color.black,
  fontWeight: theme.fontWeight.semiBold,
  lineHeight: "1.6rem",
}));

const Content = styled.span<{ isPublic: boolean }>(({ theme, isPublic }) => ({
  fontSize: "1.6rem",
  color: isPublic ? theme.color.black : theme.color.light_gray,
  minHeight: "2rem",
  whiteSpace: "pre-wrap",
  overflow: "hidden",
}));

const ReplyCount = styled.button(({ theme }) => ({
  fontSize: "1.4rem",
  lineHeight: "1.6rem",
  color: theme.color.gray,
  width: "fit-content",
}));

const ReplyButton = styled.button(({ theme }) => ({
  color: theme.color.gray,
  lineHeight: "1.6rem",
}));

const ReplyList = styled.div({
  display: "flex",
  flexDirection: "column",
});

const ReplyContainer = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "1rem",
  padding: "2rem",
  backgroundColor: theme.color.border_gray,
}));

type Props = {
  options: any;
  onClick?: () => void;
  selectedQnaId: number | null;
  setSelectedQnaId: React.Dispatch<React.SetStateAction<null | number>>;
};
const ProductQnACard = ({
  options,
  onClick,
  selectedQnaId,
  setSelectedQnaId,
}: Props) => {
  const { t } = useTranslation();
  const lang = window.localStorage.getItem("language");
  const [isReplyVisible, setIsReplyVisible] = useState(false);
  const { getProductQnaAnswerList } = useApiRequest();

  const { data: productQnaAnswerList } = useQuery({
    queryKey: ["productQnaAnswerList", options.qna_id],
    queryFn: () => getProductQnaAnswerList(options.qna_id),
    enabled: !!options.qna_id && options.qna_id === selectedQnaId,
  });

  const toggleReplyList = async () => {
    setSelectedQnaId(selectedQnaId === options.qna_id ? null : options.qna_id);
    setIsReplyVisible(!isReplyVisible);
  };

  const handleChangeSelectedId = (qnaId: number) => {
    setSelectedQnaId(selectedQnaId === qnaId ? null : options.qna_id);
  };

  return (
    <CardContainer onClick={onClick}>
      <NameWrapper>
        <NameBox>
          <span>{options.nickname}</span>
          <span className="time">{options.created_at}</span>
        </NameBox>

        <ReplyButton
          onClick={() => handleChangeSelectedId(options.qna_id)}
          type="button">
          {options?.question_is_public && t("답글달기")}
        </ReplyButton>
      </NameWrapper>
      <Content isPublic={options?.question_is_public}>
        {options.question_is_public
          ? options.question
          : t("이 글은 관리자에 의해 비공개 처리되었습니다.")}
      </Content>
      <ReplyCount onClick={toggleReplyList} type="button">
        {lang === "ko"
          ? `답글 ${options.answer_count}개`
          : `${options.answer_count} ${t(options.answer_count > 1 ? "답글들" : "답글")}`}
      </ReplyCount>
      {/*수정필요*/}

      {isReplyVisible && (
        <ReplyList>
          {productQnaAnswerList?.length > 0 ? (
            productQnaAnswerList?.map((reply: any, index: number) => (
              <ReplyContainer key={index}>
                <NameWrapper key={index}>
                  <NameBox>
                    <span>{reply.nickname}</span>
                    <span className="time">{reply.created_at}</span>
                  </NameBox>
                </NameWrapper>

                <Content
                  // className="ellipsis-1"
                  isPublic={reply.question_is_public}>
                  {reply.question_is_public
                    ? reply.answer
                    : t("이 글은 관리자에 의해 비공개 처리되었습니다.")}
                </Content>
              </ReplyContainer>
            ))
          ) : (
            <span>{t("답글이 없습니다.")}</span>
          )}
        </ReplyList>
      )}
    </CardContainer>
  );
};

export default ProductQnACard;
